import { Component } from 'react';

import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeactivateModal from './deactivatemodal';
import SchoolAccountModal from '../SchoolAccount/schoolaccountmodal';
import { schoolStatus } from '../../utils/enums';
import { useSchoolGroups } from 'hooks/data/useSchoolGroups';
import { triggerDataChangeRequestSurvey } from '../../utils/posthogSurveys';
import { ATSDistrictAccountSchoolsTestIds } from '../../data-testids/ATS';
import { Chip } from 'sharedComponents/Chip';

import SchoolIcon from '../../assets/icons/AccountManagementIcons/school_icon.svg';
import EditIcon from '../../assets/icons/AccountManagementIcons/edit-icon.svg';
import IconCaretDown from '../../assets/icon-down-caret-grey.svg';
import IconCaretUp from '../../assets/icon-up-caret-green.svg';
import XError from '../../assets/x-error.svg';

import auth from '../../utils/auth';

const SCHOOLSTATUSES = schoolStatus().reduce((obj, item) => {
  obj[item.value] = item.key;
  return obj;
}, {});

export default class DistrictAccountSchools extends Component {
  static propTypes = {
    createSchool: PropTypes.func.isRequired,
    deleteSchool: PropTypes.func.isRequired,
    updateSchool: PropTypes.func.isRequired,
    schools: PropTypes.array.isRequired,
  };

  state = {
    selectedSchool: {},
    displayCreate: false,
    errors: {},
    showArchivedSchools: false,
    showArchiveModal: false,
    showWarning: false,
  };

  editSchool = (school) => this.setState({ selectedSchool: school });

  hideModal = () => this.setState({ selectedSchool: {}, displayCreate: false });

  handleCreateSchool = (school) => {
    this.hideModal();
    this.props.createSchool(school);
  };

  handleUpdateSchool = (school) => {
    this.hideModal();
    this.props.updateSchool(school);
  };

  toggleActiveSchools = () => {
    this.setState((prevState) => {
      return { showArchivedSchools: !prevState.showArchivedSchools };
    });
  };

  openArchiveModal = () => {
    this.setState((prevState) => {
      let showWarning = false;
      if (prevState.selectedSchool.school_admins_exist) {
        // user is trying to archive a school that has admin users, so show a modal
        // telling them to remove the users first.
        showWarning = true;
      }
      return {
        showWarning: showWarning,
        showArchiveModal: true,
      };
    });
  };

  closeArchiveModal = () => {
    this.setState({
      showWarning: false,
      showArchiveModal: false,
    });
  };

  archiveSchool = () => {
    let school = Object.assign({}, this.state.selectedSchool);
    school.status = SCHOOLSTATUSES.archived;
    this.setState({ selectedSchool: school });
    this.props.updateSchool(school);
  };

  unarchiveSchool = (school) => {
    school.status = SCHOOLSTATUSES.active;
    this.props.updateSchool(school);
  };

  displayEditButton = (school) => {
    return !school.school_nces_id;
  };

  renderArchiveModal = (school) => {
    if (this.state.showWarning) {
      // the user tried to archive a school that has admins associated with it.
      // render this modal to tell them to get their act together.
      // eng note: this is funky because the user is already two modals deep
      // and the second one is based on school data.
      return (
        <DeactivateModal
          show={this.state.showArchiveModal}
          // hide both archived modal and school edit modal
          onHide={() => {
            this.closeArchiveModal();
            this.hideModal();
          }}
          // if they click back, toggle the archive modal but don't
          // close the school edit modal
          onCancel={this.closeArchiveModal}
          // the save button just says "Got it!" and doesn't actually save,
          // since the message told them there are school admins still attached
          // to this school. so just close the modals.
          onSave={() => {
            this.closeArchiveModal();
            this.hideModal();
          }}
          header={'Archive School'}
          mainText={'You cannot archive a school with assigned School Admins.'}
          subText={'Please reassign School Admins at this school then try again.'}
          saveButtonText={'Got it!'}
          backButtonText={'Back'}
          icon={SchoolIcon}
        />
      );
    } else {
      return (
        <DeactivateModal
          show={this.state.showArchiveModal}
          onHide={() => {
            this.closeArchiveModal();
            this.hideModal();
          }}
          // same behavior as onHide for this modal
          onCancel={() => {
            this.closeArchiveModal();
            this.hideModal();
          }}
          onSave={() => {
            this.archiveSchool();
            this.closeArchiveModal();
            this.hideModal();
          }}
          header={'Archive School'}
          mainText={`Are you sure you want to archive ${school.name}?`}
          subText={'This can be undone by clicking "Show Archived Schools" any time.'}
          saveButtonText={'Archive This School'}
          backButtonText={'Cancel'}
          icon={SchoolIcon}
        />
      );
    }
  };

  render() {
    return (
      <div className="card no-padding">
        <div className="header-div">
          <div className="flex">
            <img className="mr1" src={SchoolIcon} alt="" />
            <h3>Manage Schools</h3>
          </div>
          <button className="edit-button" onClick={() => this.setState({ displayCreate: true })}>
            + Add school
          </button>
        </div>
        <div className="districtaccount-row header schools">
          <div style={{ flex: '0 0 30%' }} className="name">
            School Name
          </div>
          <div style={{ minWidth: '20px' }}></div>
          <div style={{ flex: '0 0 30%' }} className="location">
            School Location
          </div>
          {/* Only district/super admins in non-posting-only districts can view groups  */}
          {auth.isDistrictAdmin() && !auth.inPostingOnlyDistrict() && (
            <div style={{ flex: '0 0 25%' }} className="groups">
              Groups
            </div>
          )}
        </div>
        {this.props.schools
          .filter((s) => s.status === SCHOOLSTATUSES.active)
          .map((school) => (
            <div
              style={{ overflow: 'auto' }}
              key={school.id}
              className="districtaccount-row schools"
            >
              <p style={{ flex: '0 0 30%' }} className="name">
                {school.name}
              </p>
              <p style={{ minWidth: '20px' }}>
                {!school.geocoded_location && (
                  <img src={XError} alt="error" style={{ width: '8px' }} />
                )}
              </p>
              <p style={{ flex: '0 0 30%' }} className="location">
                {school.location}
              </p>
              {/* Only district/super admins in non-posting-only districts can view groups */}
              {auth.isDistrictAdmin() && !auth.inPostingOnlyDistrict() && (
                <SchoolGroupsCell schoolId={school.id} />
              )}
              {this.displayEditButton(school) ? (
                <img
                  className="edit"
                  src={EditIcon}
                  alt="edit"
                  onClick={() => this.editSchool(school)}
                />
              ) : (
                <Tooltip title="This school's data is synced from NCES. Click to request a change from Nimble.">
                  <span>
                    <Chip
                      label="Request Change"
                      icon={<EditOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                      clickable={true}
                      variant="outlined"
                      size="small"
                      dataTestId={`${ATSDistrictAccountSchoolsTestIds.REQUEST_CHANGE_BUTTON}-${school.id}`}
                      onClickFunction={() => {
                        const user = auth.getUser();
                        triggerDataChangeRequestSurvey({
                          requester_districtId: user?.profile?.district?.id,
                          requester_districtName: user?.profile?.district?.name,
                          target_schoolId: school.id,
                          target_schoolName: school.name,
                          target_schoolNcesId: school.school_nces_id,
                          requestType: 'school_update',
                        });
                      }}
                      sx={{
                        cursor: 'pointer',
                        height: '28px',
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '& .MuiChip-label': {
                          padding: '0 10px 0 6px',
                        },
                        '& .MuiChip-icon': {
                          color: 'primary.main',
                          marginLeft: '8px',
                          marginRight: '-2px',
                        },
                        '&:hover': {
                          backgroundColor: 'primary.fill',
                          borderColor: 'primary.dark',
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              )}
              {this.state.selectedSchool.id === school.id && (
                <SchoolAccountModal
                  school={school}
                  displayModal={
                    this.state.selectedSchool.id === school.id && !this.state.showArchiveModal
                  }
                  errors={this.state.errors}
                  onHide={this.hideModal}
                  onSave={this.handleUpdateSchool}
                  openArchiveModal={this.openArchiveModal}
                />
              )}
              {this.state.selectedSchool.id === school.id && this.renderArchiveModal(school)}
            </div>
          ))}
        {auth.isSuperAdminUser() &&
          this.props.schools.filter((s) => s.status === SCHOOLSTATUSES.archived).length > 0 && (
            <div>
              <div
                className={`toggle-div ${this.state.showArchivedSchools ? 'expanded' : ''}`}
                onClick={this.toggleActiveSchools}
              >
                {this.state.showArchivedSchools ? 'Hide' : 'Show'}{' '}
                {this.props.schools.filter((s) => s.status === SCHOOLSTATUSES.archived).length}{' '}
                archived schools
                <img
                  src={this.state.showArchivedSchools ? IconCaretUp : IconCaretDown}
                  alt="show deactivated users"
                />
              </div>
              <div
                className={`card no-padding additional-section ${
                  this.state.showArchivedSchools ? 'expanded' : ''
                }`}
              >
                {this.props.schools
                  .filter((s) => s.status === SCHOOLSTATUSES.archived)
                  .map((school) => (
                    <div key={school.id} className="districtaccount-row schools">
                      <h4 className="name">{school.name}</h4>
                      <p className="location">{school.location}</p>
                      <span className="reactivate" onClick={() => this.unarchiveSchool(school)}>
                        Unarchive school
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        {this.state.displayCreate && (
          <SchoolAccountModal
            school={{ name: '', location: '', school_groups: [] }}
            displayModal={this.state.displayCreate}
            errors={this.state.errors}
            onHide={this.hideModal}
            onSave={this.handleCreateSchool}
          />
        )}
      </div>
    );
  }
}

/**
 * Displays a list of the school groups of a school.
 *
 * Custom truncation as per requirements:
 * "Group One, Group Two, and x more...."
 * This was a bit hard to do in a correct way, so this is a pretty ghetto implementation
 * Ideally, we would want to draw the container, get the size of the container,
 * and then figure out how many px that size can hold and truncate accordingly.
 * This ghetto way pretty much only works on desktop, but it's good enough for now.
 */
export const formatSingleGroupName = (groupName, maxLength) => {
  return groupName.length > maxLength ? groupName.substring(0, maxLength) + '...' : groupName;
};

export const formatMultipleGroupNames = (groupNames, maxLength) => {
  let displayString = '';
  let currentLength = 0;
  let shownCount = 0;

  for (let i = 0; i < groupNames.length; i++) {
    const groupName = groupNames[i];
    const separator = i === 0 ? '' : ', ';
    const wouldBeLength = currentLength + separator.length + groupName.length;
    const potentialRemainingCount = groupNames.length - (shownCount + 1);

    // Calculate suffix length if we were to stop after adding this name
    const suffix = potentialRemainingCount > 0 ? `, and ${potentialRemainingCount} more...` : '';
    const totalLengthWithSuffix = wouldBeLength + suffix.length;

    // Only add this name if the total (including suffix) fits within maxLength
    if (totalLengthWithSuffix <= maxLength) {
      displayString += separator + groupName;
      currentLength = wouldBeLength;
      shownCount++;
    } else {
      break;
    }
  }

  const remainingCount = groupNames.length - shownCount;

  // If we couldn't fit any names, show truncated first name
  if (shownCount === 0) {
    const suffix = `, and ${remainingCount - 1} more...`;
    const ellipsis = '...';
    const availableForName = maxLength - suffix.length - ellipsis.length;
    const truncatedFirstName = groupNames[0].substring(0, Math.max(1, availableForName)) + ellipsis;
    return truncatedFirstName + suffix;
  }

  if (remainingCount > 0) {
    displayString += `, and ${remainingCount} more...`;
  }

  return displayString;
};

const SchoolGroupsCell = ({ schoolId }) => {
  const schoolGroups = useSchoolGroups();
  const MAX_CHARACTER_LENGTH = 30;

  const taggedGroups = schoolGroups.data.filter((schoolGroup) =>
    schoolGroup.schools.includes(schoolId)
  );

  if (taggedGroups.length === 0) {
    return <p style={{ flex: '0 0 20%' }} className="groups"></p>;
  }

  const groupNames = taggedGroups.map((group) => group.name);
  const displayString =
    groupNames.length === 1
      ? formatSingleGroupName(groupNames[0], MAX_CHARACTER_LENGTH)
      : formatMultipleGroupNames(groupNames, MAX_CHARACTER_LENGTH);

  return (
    <p style={{ flex: '0 0 20%' }} className="groups">
      {displayString}
    </p>
  );
};
